import React, { useRef } from 'react';
import { motion, useReducedMotion, useMotionValue, useTransform } from 'framer-motion';
import { MapPin, ChevronRight, Navigation } from 'lucide-react';

// Se muestra dentro de la vista del cliente (.cv-root): usa sus tokens por
// variables CSS (--cv-*) y sus clases (cv-display, cv-scroll), así sigue el
// modo claro/oscuro y el color de cada barbería, igual que el paso 1.
const LocationSelector = ({ locations, handleSelectLocation, loading, logoVersion, shopSettings }) => {
  const brandColor = shopSettings?.brandColor || '#F59E0B';
  const reduceMotion = useReducedMotion();
  const conFoto = !!shopSettings?.bannerUrl;

  const fallbackImage = (locations && locations.length > 0 && locations[0].image)
    ? locations[0].image
    : (shopSettings?.logoUrl || '/images/logo/barber-logo.png');

  // Mismo hero que el paso 1: la foto queda casi fija, se oscurece mientras
  // el panel la tapa y se estira con el rebote de iOS al tirar hacia abajo.
  const heroRef = useRef(null);
  const altoRef = useRef(1);
  const scrollY = useMotionValue(0);
  const progreso = (v) => Math.min(Math.max(v / altoRef.current, 0), 1);
  const fotoY = useTransform(scrollY, (v) => Math.max(v, 0) * 0.55);
  const fotoScale = useTransform(scrollY, (v) => (v < 0 ? 1 + -v / altoRef.current : 1));
  const sombra = useTransform(scrollY, (v) => progreso(v) * 0.65);
  const nombreOpacity = useTransform(scrollY, (v) => 1 - Math.min(progreso(v) * 2.2, 1));

  const abrirMapa = (e, loc) => {
    e.stopPropagation();
    const q = encodeURIComponent([loc.address, shopSettings?.shopName].filter(Boolean).join(', '));
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank', 'noopener,noreferrer');
  };

  const cantidad = locations?.length || 0;

  return (
    <div className="cv-scroll h-[100dvh] w-full overflow-y-auto overflow-x-hidden no-scrollbar"
      onScroll={(e) => {
        altoRef.current = heroRef.current?.offsetHeight || 1;
        scrollY.set(e.currentTarget.scrollTop);
      }}>

      {/* HERO — la fachada con el logo y el nombre de la barbería */}
      <div ref={heroRef} className="relative w-full"
        style={{ minHeight: conFoto ? 'min(46svh, 400px)' : 'auto', background: conFoto ? '#000' : 'transparent' }}>
        {conFoto && (
          <motion.div className="absolute inset-0 origin-bottom"
            style={reduceMotion ? undefined : { y: fotoY, scale: fotoScale, willChange: 'transform' }}>
            <motion.img
              initial={reduceMotion ? { opacity: 0 } : { scale: 1.08, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              src={shopSettings.bannerUrl}
              alt={`Fachada de ${shopSettings?.shopName || 'la barbería'}`}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.82) 100%)' }} />
            {!reduceMotion && <motion.div className="absolute inset-0" style={{ background: '#000', opacity: sombra }} />}
          </motion.div>
        )}

        <motion.div
          className={`z-10 px-5 ${conFoto ? 'absolute bottom-0 left-0 right-0 pb-[52px]' : 'relative pb-2'}`}
          style={{
            color: conFoto ? '#fff' : 'var(--cv-ink)',
            paddingTop: conFoto ? undefined : 'calc(env(safe-area-inset-top) + 72px)',
            opacity: conFoto && !reduceMotion ? nombreOpacity : 1,
          }}>
          <div className="shrink-0 overflow-hidden flex items-center justify-center"
            style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--cv-surface)', boxShadow: '0 0 0 1px var(--cv-line)' }}>
            {shopSettings?.logoUrl ? (
              <img src={shopSettings.logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="cv-display" style={{ color: brandColor, fontSize: 31 }}>
                {shopSettings?.shopName?.charAt(0) || 'B'}
              </span>
            )}
          </div>
          <h1 className="cv-display mt-4" style={{ fontSize: 'clamp(3.25rem, 15vw, 5.5rem)', textWrap: 'balance' }}>
            {shopSettings?.shopName || 'Barbería'}
          </h1>
        </motion.div>
      </div>

      {/* PANEL — con foto, sube por encima de ella al hacer scroll */}
      <div className={conFoto ? 'relative z-10 -mt-7 rounded-t-[28px] md:rounded-t-[32px]' : ''}
        style={conFoto ? { background: 'var(--cv-bg)', boxShadow: '0 -10px 30px rgba(0,0,0,0.22)' } : undefined}>
        <div className="w-full max-w-md mx-auto md:max-w-4xl px-4 pt-7 pb-[calc(env(safe-area-inset-bottom)+32px)]">

          <div className="flex items-end justify-between gap-3 mb-5 px-1">
            <h2 className="cv-display" style={{ fontSize: '2.4rem' }}>¿A qué local vas?</h2>
            {!loading && cantidad > 0 && (
              <span className="shrink-0 pb-1 text-[14px] font-medium cv-num" style={{ color: 'var(--cv-ink-soft)' }}>
                {cantidad} {cantidad === 1 ? 'local' : 'locales'}
              </span>
            )}
          </div>

          {/* Cargando: tarjetas esqueleto con la misma forma que las reales */}
          {loading && cantidad === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" aria-label="Cargando locales">
              {[1, 2].map(i => (
                <div key={i} className="rounded-[20px] overflow-hidden" style={{ background: 'var(--cv-surface)', boxShadow: '0 0 0 1px var(--cv-line)' }}>
                  <div className="aspect-[16/9] animate-pulse" style={{ background: 'var(--cv-sunk)' }} />
                  <div className="p-4 flex flex-col gap-2">
                    <div className="h-5 w-1/2 rounded animate-pulse" style={{ background: 'var(--cv-sunk)' }} />
                    <div className="h-4 w-3/4 rounded animate-pulse" style={{ background: 'var(--cv-sunk)' }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* LOCALES */}
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(locations || []).map((loc, index) => (
              <motion.li key={loc.id}
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index, 6) * 0.05, duration: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                <motion.div
                  role="button" tabIndex={0}
                  aria-label={`Elegir ${loc.name}`}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelectLocation(loc)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectLocation(loc); } }}
                  className="cv-row rounded-[20px] overflow-hidden cursor-pointer"
                  style={{ background: 'var(--cv-surface)', boxShadow: '0 0 0 1px var(--cv-line)' }}>

                  <div className="relative aspect-[16/9] overflow-hidden" style={{ background: 'var(--cv-sunk)' }}>
                    {loc.image ? (
                      <img
                        key={`${loc.image}-${logoVersion}`}
                        src={`${loc.image}?v=${logoVersion}`}
                        alt=""
                        loading={index > 1 ? 'lazy' : 'eager'}
                        draggable={false}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <MapPin size={34} style={{ color: 'var(--cv-ink-faint)' }} />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 p-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[17px] font-bold truncate">{loc.name}</h3>
                      {loc.address && (
                        <p className="mt-0.5 flex items-start gap-1.5 text-[14px]" style={{ color: 'var(--cv-ink-soft)' }}>
                          <MapPin size={14} className="shrink-0 mt-[3px]" />
                          <span className="line-clamp-2">{loc.address}</span>
                        </p>
                      )}
                    </div>
                    {loc.address && (
                      <button type="button" onClick={(e) => abrirMapa(e, loc)}
                        aria-label={`Cómo llegar a ${loc.name}`}
                        className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                        style={{ background: 'var(--cv-sunk)', color: 'var(--cv-ink)' }}>
                        <Navigation size={17} />
                      </button>
                    )}
                    <ChevronRight size={20} className="shrink-0" style={{ color: 'var(--cv-ink-faint)' }} />
                  </div>
                </motion.div>
              </motion.li>
            ))}
          </ul>

          {/* FOOTER */}
          <a href="https://barbergo.com.py" target="_blank" rel="noopener noreferrer"
            className="mt-12 mx-auto w-fit flex items-center gap-2.5 h-11 px-4 rounded-full text-[13px] font-medium"
            style={{ color: 'var(--cv-ink-faint)' }}>
            Hecho con
            {/* Solo el ícono del logo (el texto "Barber" es blanco y no se ve en tema claro) */}
            <span className="relative block overflow-hidden shrink-0" style={{ width: 21, height: 20 }} aria-hidden="true">
              <img
                src="https://res.cloudinary.com/dz5ibyt7v/image/upload/v1793406258/barbergo-logo_tpj2tg.png"
                alt="" draggable={false}
                style={{ position: 'absolute', height: 20, maxWidth: 'none', left: 0, top: 0 }}
              />
            </span>
            <span className="font-bold" style={{ color: 'var(--cv-ink-soft)' }}>BarberGo</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
